import { dcrClientPending } from "../../shared/connectors/dcr";
import { connectorsByIdentifier } from "../../shared/connectors";
import { isConfigured } from "../../shared/connectors/isDraft";
import type { Connector } from "./connector";

export type ConnectorStatus = "active" | "untested" | "error" | "draft";

export const connectorStatusColors = {
  active: "green",
  untested: "gray",
  error: "red",
  draft: "gray",
} as const satisfies Record<ConnectorStatus, string>;

export const MISSING_WORKFLOW_ERROR =
  "The selected workflow no longer exists. Choose another workflow.";

export function getConnectorStatus(app: Connector): ConnectorStatus {
  const configured = isConfigured(app);

  if (app.identifier === "workflow") {
    if (!app.backingWorkflowId && !app.fresh) {
      return "error";
    }
    return configured ? "active" : "draft";
  }

  if (app.authorizationMode === "visitor" && oauthSetupError(app)) {
    return "error";
  }
  if (!configured) return "draft";
  if (app.authorizationMode === "visitor") return "active";
  // A dead OAuth connection outranks a stale test success: the refresh loop
  // has already proven the credential no longer works.
  if (app.oauthConnection?.status === "error") return "error";
  if (app.lastTestStatus === "failure") return "error";
  if (!app.lastTestStatus || app.lastTestStatus === "pending") {
    return "untested";
  }
  return "active";
}

export function oauthSetupError(
  app: Pick<
    Connector,
    "identifier" | "configuration" | "oauthClientId" | "oauthConnection"
  >
) {
  return dcrClientPending(
    connectorsByIdentifier[app.identifier]?.oauth,
    app.configuration,
    app.oauthClientId
  )
    ? (app.oauthConnection?.lastError ?? null)
    : null;
}

export function getConnectorStatusTooltip(
  app: Connector,
  status: ConnectorStatus
): string | null {
  if (status === "draft") {
    return app.identifier === "workflow"
      ? "Choose a workflow"
      : "Finish connector setup";
  }
  if (app.authorizationMode === "visitor") {
    if (status === "error") return oauthSetupError(app);
    if (status === "active") return "Each caller connects their own account.";
  }
  if (app.identifier !== "workflow") return null;
  if (status === "error") return "Workflow missing. Choose another.";
  return null;
}

export function grantStatus(status: string): {
  color: "green" | "red" | "gray";
  label: string;
} {
  if (status === "connected") return { color: "green", label: "Connected" };
  if (status === "error") return { color: "red", label: "Error" };
  return { color: "gray", label: "Pending" };
}
